import React, { useRef, useLayoutEffect, useState } from "react";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import gsap from "gsap";

import {
  h3InnerStyles,
  centerAlignStyles,
  technoOrange,
  pStyles,
} from "../styles/global.module.css";

import {
  strategyArtifactsStyles,
  dlStrategyArtifactsStyles,
  firstTermStyles,
  secondTermStyles,
  dtStrategyArtifactsStyles,
  ddStrategyArtifactsStyles,
  pStrategyArtifactsStyles,
  svgNumberStyles,
  leftArrowStyles,
  rightArrowStyles,
} from "../styles/strategy-artifacts.module.css";

function StrategyArtifacts({ getStandAloneTimelines }) {
  const leftClicked = useRef();
  const rightClicked = useRef();
  const breakpoints = useBreakpoint();
  const tlStrategyArtifacts = useRef();
  const [activeTab, setActiveTab] = useState("first-tab");
  tlStrategyArtifacts.current = gsap.timeline();

  useLayoutEffect(() => {
    let parentElement = document.getElementById("strategy-artifacts");
    let children = parentElement.childNodes;
    tlStrategyArtifacts.current.from(children, {
      duration: 1,
      opacity: 0,
      stagger: { each: 0.2 },
    });
    getStandAloneTimelines(
      tlStrategyArtifacts.current,
      "tl-strategy-artifacts"
    );

    return function cleanupListener() {
      try {
        tlStrategyArtifacts.current.kill();
      } catch (e) {
        // Code to run if an exception (error) occurs
      }
    };
  }, []);

  useLayoutEffect(() => {
    const onLeftClick = () => {
      if (activeTab !== "first-tab") {
        switch (activeTab) {
          case "second-tab":
            gsap.set(["#third-tab", "#fourth-tab"], { display: "none" });
            gsap.to("#second-tab", {
              gridArea: "next",
              opacity: 0.5,
              justifySelf: "start",
              width: "6em",
            });
            gsap.to("#first-tab", {
              gridArea: "active",
              opacity: 1,
              display: "flex",
              overflow: "hidden",
              justifySelf: "center",
              width: "6em",
            });
            gsap.to("#right path", { fillOpacity: 1 });
            gsap.to("#right", { cursor: "pointer" });
            gsap.to("#left path", { fillOpacity: 0.1 });
            gsap.to("#left", { cursor: "default" });
            setActiveTab("first-tab");
            break;
        }
      }
    };

    const onRightClick = () => {
      if (activeTab !== "second-tab") {
        switch (activeTab) {
          case "first-tab":
            gsap.to("#first-tab", {
              gridArea: "previous",
              opacity: 0.5,
              justifySelf: "end",
              width: "6em",
            });
            gsap.to("#second-tab", {
              gridArea: "active",
              opacity: 1,
              justifySelf: "center",
              width: "6em",
            });
            gsap.to("#right path", { fillOpacity: 0.1 });
            gsap.to("#right", { cursor: "default" });
            gsap.to("#left path", { fillOpacity: 1 });
            gsap.to("#left", { cursor: "pointer" });
            setActiveTab("second-tab");
            break;
        }
      }
    };

    rightClicked.current = onRightClick;
    leftClicked.current = onLeftClick;

    if (activeTab) {
      gsap.to(".active-tab", { duration: 0.1, opacity: 1 });
      gsap.to(".inactive-tab", { duration: 0.1, opacity: 0 });
      gsap.to("#strategy-artifacts dt", {
        duration: 0.1,
        borderBottom: "3px solid #1A1A1A",
      });
      gsap.to(`#${activeTab}`, {
        duration: 0.1,
        borderBottom: "3px solid #fc4d0d",
        repeat: 3,
      });
      gsap.to("#strategy-artifacts dt path", {
        duration: 0.1,
        fill: "#ffffff",
        opacity: 0.15,
      });
      gsap.to(`#${activeTab} path`, {
        duration: 0.1,
        fill: "#fc4d0d",
        repeat: 3,
        opacity: 0.4,
      });
    }
  }, [activeTab]);
  return (
    <section
      id="strategy-artifacts"
      className={`section ${strategyArtifactsStyles}`}
    >
      <h3 className={`${h3InnerStyles} ${centerAlignStyles}`}>
        Strategy <span className={`${technoOrange}`}>Artifacts</span>
      </h3>
      <dl className={dlStrategyArtifactsStyles}>
        {breakpoints.sm ? (
          <svg
            className={leftArrowStyles}
            onClick={() => leftClicked.current()}
            id="left"
            width="34"
            height="143"
            viewBox="0 0 34 143"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M33.25 140.718L0.55298 71.5L33.25 2.28201L33.25 140.718Z"
              fill="#FC4D0D"
              fillOpacity="0.1"
              stroke="#FC4D0D"
            />
          </svg>
        ) : null}
        {breakpoints.sm ? (
          <svg
            className={rightArrowStyles}
            onClick={() => rightClicked.current()}
            id="right"
            width="34"
            height="143"
            viewBox="0 0 34 143"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.749999 2.28201L33.447 71.5L0.749993 140.718L0.749999 2.28201Z"
              fill="#FC4D0D"
              fillOpacity="1"
              stroke="#FC4D0D"
            />
          </svg>
        ) : null}
        <dt
          id="first-tab"
          className={`${firstTermStyles} ${dtStrategyArtifactsStyles} ${h3InnerStyles}`}
          onClick={breakpoints.sm ? null : () => setActiveTab("first-tab")}
        >
          <span className={technoOrange}>Digital</span> Roadmap
          <svg
            className={`numbers ${svgNumberStyles}`}
            width="200"
            height="175"
            viewBox="0 0 85 114"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              opacity="0.15"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M85 87.9043V113.325L3.90686 113.325L3.90686 87.9043H31.6455L31.6455 31.8633C29.9966 33.4091 27.7656 34.9942 24.9855 36.6244C21.2843 38.8709 17.1941 40.783 12.7181 42.363C8.35642 43.9487 4.41016 44.7594 0.891976 44.7594H0L0 18.5576H0.891976C3.06366 18.5576 5.73294 17.8914 8.92516 16.4876C12.2703 14.9432 15.553 13.1409 18.7734 11.0799L18.78 11.0756L18.7867 11.0715C22.1458 9.0044 24.9735 7.01076 27.2765 5.09175C29.7515 3.00738 31.1948 1.47212 31.7578 0.458796L32.0127 0L60.1915 0L60.1915 87.9043H85ZM27.8493 5.77556C25.5052 7.72898 22.6401 9.74753 19.2542 11.8312C15.9985 13.9148 12.6777 15.738 9.29172 17.3008C6.036 18.7333 3.23609 19.4496 0.891976 19.4496L0.891976 43.8674C1.18587 43.8674 1.48319 43.8615 1.78395 43.8497C4.94826 43.7259 8.49268 42.9504 12.4172 41.5233C16.845 39.9605 20.8821 38.0722 24.5285 35.8583C27.6637 34.0204 30.0361 32.2723 31.6455 30.6139C31.9748 30.2746 32.2721 29.9391 32.5375 29.6074L32.5375 88.7962H4.79883L4.79883 112.433L84.108 112.433V88.7962H59.2995L59.2995 0.891976L32.5375 0.891976C31.8864 2.06404 30.3236 3.6919 27.8493 5.77556Z"
              fill="white"
            />
          </svg>
        </dt>
        <dd className={ddStrategyArtifactsStyles}>
          <p
            className={`${pStyles} ${pStrategyArtifactsStyles} ${
              activeTab === "first-tab" ? "active-tab" : "inactive-tab"
            }`}
          >
            Our Digital Roadmap artifact documents the basis of our strategies
            and gives context for all initial decisions. Our approach is to
            define thorougly, but loosely and iterate quickly as we acquire more
            knowledge during the course of a build. At the beginning of a build
            there are a lot of hypotheses that need to be validated. Documenting
            our strategies clearly means our initial trajectory will most likely
            yield favorable results even before our hypotheses are validated
            with data. As we gain further insight during the course of the build
            we adapt and modify this artifact to reflect the new findings. All
            technologies selected, infrastructure, projected timelines and
            project risks are included here.
          </p>
        </dd>
        <dt
          id="second-tab"
          className={`${secondTermStyles} ${dtStrategyArtifactsStyles} ${h3InnerStyles}`}
          onClick={breakpoints.sm ? null : () => setActiveTab("second-tab")}
        >
          <span className={technoOrange}>Discovery</span> Campaign
          <svg
            className={`numbers ${svgNumberStyles}`}
            width="200"
            height="175"
            viewBox="0 0 95 115"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              opacity="0.15"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0 114.151L0 113.272C0 105.6 0.84499 98.7467 2.54795 92.7209L2.55034 92.7124L2.55291 92.704C4.39852 86.6398 7.56227 81.1732 12.0321 76.309C16.4993 71.4477 22.7728 66.7518 30.8182 62.21L30.8292 62.2038L30.8403 62.1979C37.9319 58.4588 43.8613 55.3007 48.6294 52.7233C53.3684 50.1617 56.904 47.7543 59.275 45.5084C61.6995 43.205 62.8702 40.591 62.8702 37.6342C62.8702 34.0364 61.4457 30.9834 58.5278 28.4302L58.5201 28.4235L58.5126 28.4166C55.6295 25.7737 51.4853 24.3919 45.9524 24.3919C39.9102 24.3919 34.7333 25.8369 30.3841 28.6935C26.0663 31.615 22.3259 34.9766 19.1585 38.7775L18.4964 39.572L0.853421 19.2307L1.40165 18.652C3.7986 16.1219 7.35383 13.4313 12.0276 10.5751C16.7426 7.69377 22.3551 5.21682 28.8546 3.13699C35.4008 1.04219 42.5856 0 50.4017 0C63.6822 0 74.0996 3.1241 81.5324 9.49184C89.085 15.7224 92.8721 23.8379 92.8721 33.7653C92.8721 39.9662 91.4194 45.3013 88.4704 49.726C85.7015 53.9446 82.2084 57.568 77.9975 60.5946L77.9932 60.5977C73.9771 63.4478 70.0867 65.9768 66.3221 68.1836L66.2969 68.1984L66.2707 68.2115C58.0334 72.3301 51.5547 75.9235 46.8225 78.9927C42.7397 81.7154 39.377 85.0455 36.7276 88.9856L95 88.9856V114.151L0 114.151ZM35.1194 89.8649C35.3004 89.5686 35.4852 89.2755 35.6736 88.9856C38.4376 84.7315 41.9928 81.1557 46.3393 78.258C51.111 75.1629 57.6237 71.5519 65.8775 67.425C69.6174 65.2326 73.4864 62.7178 77.4843 59.8806C81.6111 56.9144 85.0287 53.3679 87.7369 49.241C90.5742 44.9852 91.9928 39.8266 91.9928 33.7653C91.9928 24.0929 88.3173 16.2261 80.9663 10.1648C73.7443 3.97445 63.5561 0.879304 50.4017 0.879304C42.6638 0.879304 35.5708 1.91103 29.1225 3.97446C22.6743 6.03789 17.1288 8.48822 12.4861 11.3254C7.84339 14.1627 4.36135 16.8064 2.03998 19.2568L18.483 38.2146C18.6728 37.9868 18.8646 37.7606 19.0584 37.536C22.157 33.9448 25.7697 30.7534 29.8963 27.9619C34.4101 24.9957 39.7621 23.5126 45.9524 23.5126C51.6269 23.5126 56.0117 24.9312 59.1068 27.7684C62.202 30.4767 63.7495 33.7653 63.7495 37.6342C63.7495 40.8583 62.4599 43.6955 59.8806 46.1459C57.4303 48.4672 53.8193 50.9176 49.0476 53.4968C44.2759 56.0762 38.3435 59.2358 31.2505 62.9758C23.2547 67.4895 17.0644 72.1322 12.6796 76.9039C8.29477 81.6756 5.19962 87.0276 3.39411 92.96C1.78243 98.6629 0.945417 105.14 0.883067 112.393C0.880558 112.684 0.879304 112.978 0.879304 113.272L94.1207 113.272V89.8649L35.1194 89.8649Z"
              fill="white"
            />
          </svg>
        </dt>
        <dd className={ddStrategyArtifactsStyles}>
          <p
            className={`${pStyles} ${pStrategyArtifactsStyles} ${
              activeTab === "second-tab" ? "active-tab" : "inactive-tab"
            }`}
          >
            Our Discovery Campaign artifact outlines the first of many campaigns
            to gather information about your current or prospective audience.
            The focus of this campaign is validating some of our inital
            hypotheses about your audience. During our deep dive session we
            formulate several hypotheses which need to be validated. Validating,
            refining and building on these hypotheses are a key part of moving
            your business along the roadmap to achieving your digital goals.
          </p>
        </dd>
      </dl>
    </section>
  );
}

export default StrategyArtifacts;
